import {
  IconCheck,
  IconHelp,
  IconHelpFilled,
  IconX,
} from "@tabler/icons-react";
import {
  Box,
  Center,
  Group,
  TextInput, // Remplacé PasswordInput par TextInput
  Popover,
  Progress,
  Text,
  HoverCard,
  ThemeIcon,
  List,
  ListItem,
  Stack,
} from "@mantine/core";
import { useInputState } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../stores/LanguageStore";

function Requirement({ meets, label }) {
  return (
    <Text component="div" c={meets ? "teal" : "red"} mt={5} size="sm">
      <Center inline>
        {meets ? (
          <IconCheck size="0.9rem" stroke={1.5} />
        ) : (
          <IconX size="0.9rem" stroke={1.5} />
        )}
        <Box ml={7}>{label}</Box>
      </Center>
    </Text>
  );
}

const SmsSenderField = ({
  id,
  size,
  leftSection,
  label,
  placeholder,
  additionalOnchange,
  wrapperProps,
  defaultValue,
  ...props
}) => {
  const lang = useLanguage((s) => s.language);
  const [error, setError] = useState(false);
  const [value, setValue] = useInputState(defaultValue || "");
  const [focused, setFocused] = useState(false);

  // Définition des 3 règles strictes pour le Sender ID
  const requirements = useMemo(() => {
    return [
      {
        meets: value.length >= 4 && value.length <= 11,
        label: lang.model_sms.sender_helper.min_4_chars,
      },
      {
        meets: /[a-zA-Z]/.test(value),
        label: lang.model_sms.sender_helper.min_1_letter_char,
      },
      {
        meets: value.length > 0 && /^[a-zA-Z0-9]+$/.test(value),
        label: lang.model_sms.sender_helper.no_special_char,
      },
    ];
  }, [value]);

  // Calcul du score (0, 33, 66, ou 100)
  const strength = useMemo(() => {
    const metCount = requirements.filter((req) => req.meets).length;
    return (metCount / requirements.length) * 100;
  }, [requirements]);

  const checks = requirements.map((requirement, index) => (
    <Requirement
      key={index}
      label={requirement.label}
      meets={requirement.meets}
    />
  ));

  const bars = Array(requirements.length)
    .fill(0)
    .map((_, index) => (
      <Progress
        styles={{ section: { transitionDuration: "200ms" } }}
        value={strength >= ((index + 1) / requirements.length) * 100 ? 100 : 0}
        color={strength === 100 ? "teal" : strength > 50 ? "yellow" : "red"}
        key={index}
        size={4}
      />
    ));

  useEffect(() => {
    if (strength === 100) {
      setError(false);
    } else if (value.length > 0) {
      setError(true);
    }
  }, [strength, value.length, setError]);

  return (
    <Popover
      position={"bottom-start"}
      withArrow
      opened={focused}
      onChange={setFocused}
      offset={0}
      zIndex={1150}
    >
      <Popover.Target>
        <Group w={"100%"} align="center" {...wrapperProps}>
          <TextInput
            error={value && strength < 100 ? error || "Format invalide" : false}
            id={id}
            value={value}
            onChange={(v) => {
              // Optionnel: on peut forcer la suppression des espaces en direct
              const cleanedValue = v.target.value.replace(/\s/g, "");
              additionalOnchange?.(cleanedValue);
              setValue(cleanedValue);
            }}
            placeholder={placeholder || "MonEntreprise"}
            label={label || "Expéditeur SMS (Sender ID)"}
            leftSection={leftSection}
            name={name}
            w={"100%"}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            size={size}
            radius={"md"}
            pattern="^(?=.*[a-zA-Z])[a-zA-Z0-9]{4,11}$"
            // title="Caractères alphanumériques sans accents, entre 4 et 11 caractères, contenant au moins une lettre et aucun caractère spécial."
            maxLength={11}
            minLength={4}
            rightSection={
              <HoverCard position="bottom" withArrow width={450} offset={0}>
                <HoverCard.Target>
                  <ThemeIcon color="secondary" size={"xl"}>
                    <IconHelpFilled />
                  </ThemeIcon>
                </HoverCard.Target>

                <HoverCard.Dropdown>
                  <Stack gap={4} pr={16}>
                    <Text>{lang.model_sms.sender_helper.intro}</Text>
                    <List>
                      <ListItem>
                        {lang.model_sms.sender_helper.option_1}
                      </ListItem>
                      <ListItem>
                        {lang.model_sms.sender_helper.option_2}
                      </ListItem>
                    </List>
                  </Stack>
                </HoverCard.Dropdown>
              </HoverCard>
            }
            {...props}
          />
        </Group>
      </Popover.Target>

      <Popover.Dropdown p={"xs"}>
        <Group gap={5} grow mt="xs" mb="md">
          {bars}
        </Group>
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
};

export default SmsSenderField;
